import { useEffect, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import { apiFetch } from '../lib/api'

export default function ReferAndEarn({ currentUser, onLogin }) {
  const [data, setData] = useState(null)
  const [bankDetail, setBankDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [showBankForm, setShowBankForm] = useState(false)
  const [bankForm, setBankForm] = useState({ accountName: '', accountNumber: '', ifscCode: '', bankName: '', upiId: '' })
  const [bankSaving, setBankSaving] = useState(false)
  const [bankMsg, setBankMsg] = useState('')
  const qrRef = useRef(null)

  useEffect(() => {
    if (!currentUser) { setLoading(false); return }
    Promise.all([
      apiFetch('/affiliate/my-referral').then(r => r.ok ? r.json() : null),
      apiFetch('/affiliate/bank-details').then(r => r.ok ? r.json() : null),
    ]).then(([ref, bd]) => {
      setData(ref)
      const detail = bd?.bankDetail
      setBankDetail(detail)
      if (detail) {
        setBankForm({
          accountName: detail.accountName || '',
          accountNumber: detail.accountNumber || '',
          ifscCode: detail.ifscCode || '',
          bankName: detail.bankName || '',
          upiId: detail.upiId || '',
        })
      }
    }).finally(() => setLoading(false))
  }, [currentUser])

  function copyLink() {
    if (!data?.referralLink) return
    navigator.clipboard.writeText(data.referralLink).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    })
  }

  function downloadQr() {
    const canvas = qrRef.current
    if (!canvas) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = `coeater-referral-${data?.referralCode || 'qr'}.png`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  async function saveBankDetails(e) {
    e.preventDefault()
    setBankSaving(true)
    setBankMsg('')
    try {
      const r = await apiFetch('/affiliate/bank-details', {
        method: 'POST',
        body: JSON.stringify(bankForm),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.message || 'Failed to save.')
      setBankMsg('Bank details saved!')
      setBankDetail({ ...bankForm })
      setShowBankForm(false)
    } catch (err) {
      setBankMsg(err.message)
    } finally {
      setBankSaving(false)
    }
  }

  if (!currentUser) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <p className="text-4xl mb-4">🎁</p>
        <h2 className="title-font text-2xl font-bold text-gray-900 mb-2">Refer & Earn</h2>
        <p className="text-gray-500 mb-6">Login to get your referral link and start earning.</p>
        <button onClick={onLogin} className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-6 py-3 text-sm font-semibold text-white">
          Login / Sign Up
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center text-sm text-gray-500">
        Loading referral info…
      </div>
    )
  }

  const stats = data?.stats || {}
  const link = data?.referralLink || ''
  // Tag QR links so scans can be told apart from shared-link clicks
  const qrLink = link ? `${link}${link.includes('?') ? '&' : '?'}src=qr` : ''

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm text-center">
        <p className="text-5xl mb-3">🎁</p>
        <h1 className="title-font text-2xl font-bold text-gray-900">Refer & Earn</h1>
        <p className="mt-2 text-sm text-gray-500">
          Share your link. Each friend who joins earns you{' '}
          <span className="font-bold text-orange-600">₹{data?.payoutPerReferral ?? 5}</span> once admin processes the payout.
        </p>
      </div>

      {/* Referral link */}
      <div className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-wide text-orange-600">Your Referral Link</h2>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={link}
            className="flex-1 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2 text-sm text-gray-700 truncate"
          />
          <button
            onClick={copyLink}
            className="shrink-0 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 transition"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`Hey! Join me on Co-Eater — the app to find dining partners! Sign up here: ${link}`)}`}
            target="_blank" rel="noopener noreferrer"
            className="rounded-full border border-green-300 px-4 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-50"
          >
            📱 Share via WhatsApp
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Join Co-Eater – find dining partners near you! ${link}`)}`}
            target="_blank" rel="noopener noreferrer"
            className="rounded-full border border-sky-300 px-4 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50"
          >
            𝕏 Share on X
          </a>
        </div>
        <p className="text-xs text-gray-400">Referral code: <span className="font-mono font-bold text-orange-600">{data?.referralCode}</span></p>
      </div>

      {/* Referral QR code */}
      {link && (
        <div className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm space-y-3 text-center">
          <h2 className="text-sm font-bold uppercase tracking-wide text-orange-600">Your Referral QR Code</h2>
          <p className="text-xs text-gray-500">Friends can scan this to sign up with your referral link.</p>
          <div className="flex justify-center">
            <div className="rounded-2xl border border-orange-100 bg-white p-3">
              <QRCodeCanvas
                ref={qrRef}
                value={qrLink}
                size={512}
                marginSize={2}
                level="M"
                style={{ width: 200, height: 200 }}
              />
            </div>
          </div>
          <button
            onClick={downloadQr}
            className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 transition"
          >
            ⬇ Download QR Code
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: 'Total Referrals', value: stats.totalReferrals ?? 0, color: 'text-gray-900' },
          { label: 'Pending (₹)', value: `₹${(stats.pendingAmount ?? 0).toFixed(0)}`, color: 'text-yellow-600' },
          { label: 'Paid (₹)', value: `₹${(stats.paidAmount ?? 0).toFixed(0)}`, color: 'text-green-600' },
          { label: 'Total Earned', value: `₹${(stats.totalEarned ?? 0).toFixed(0)}`, color: 'text-orange-600' },
        ].map(s => (
          <div key={s.label} className="rounded-2xl border border-orange-100 bg-white/80 p-4 text-center shadow-sm">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Bank details */}
      <div className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wide text-orange-600">Bank / UPI Details</h2>
          <button
            onClick={() => { setShowBankForm(v => !v); setBankMsg('') }}
            className="text-xs font-semibold text-orange-600 hover:underline"
          >
            {showBankForm ? 'Cancel' : bankDetail ? 'Edit' : '+ Add'}
          </button>
        </div>

        {!showBankForm && bankDetail && (
          <div className="space-y-1 text-sm text-gray-700">
            <p><span className="font-medium">Name:</span> {bankDetail.accountName}</p>
            <p><span className="font-medium">Bank:</span> {bankDetail.bankName}</p>
            <p><span className="font-medium">Account:</span> ••••{bankDetail.accountNumber?.slice(-4)}</p>
            <p><span className="font-medium">IFSC:</span> {bankDetail.ifscCode}</p>
            {bankDetail.upiId && <p><span className="font-medium">UPI:</span> {bankDetail.upiId}</p>}
          </div>
        )}

        {!showBankForm && !bankDetail && (
          <p className="text-sm text-gray-400">No bank details saved yet. Add them so admin can process your payout.</p>
        )}

        {showBankForm && (
          <form onSubmit={saveBankDetails} className="space-y-3">
            {[
              { key: 'accountName', label: 'Account Holder Name', placeholder: 'Full name as on bank account' },
              { key: 'bankName', label: 'Bank Name', placeholder: 'e.g. State Bank of India' },
              { key: 'accountNumber', label: 'Account Number', placeholder: '1234567890' },
              { key: 'ifscCode', label: 'IFSC Code', placeholder: 'SBIN0001234' },
              { key: 'upiId', label: 'UPI ID (optional)', placeholder: 'yourname@upi', required: false },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{f.label}</label>
                <input
                  type="text"
                  value={bankForm[f.key]}
                  onChange={e => setBankForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  required={f.required !== false}
                  className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>
            ))}
            {bankMsg && <p className={`text-sm font-medium ${bankMsg.includes('saved') ? 'text-green-600' : 'text-red-500'}`}>{bankMsg}</p>}
            <button
              type="submit"
              disabled={bankSaving}
              className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {bankSaving ? 'Saving…' : 'Save Details'}
            </button>
          </form>
        )}
        {bankMsg && !showBankForm && (
          <p className="text-sm font-medium text-green-600">{bankMsg}</p>
        )}
      </div>

      {/* Payout history */}
      {data?.payouts?.length > 0 && (
        <div className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-wide text-orange-600">Payout History</h2>
          <div className="space-y-2">
            {data.payouts.map(p => (
              <div key={p.id} className="flex items-center justify-between rounded-xl bg-orange-50 px-4 py-3 text-sm">
                <div>
                  <p className="font-semibold text-gray-800">
                    Referral #{p.id}
                    {p.amount > 0 && <span className="ml-2 text-orange-600 font-bold">₹{p.amount}</span>}
                  </p>
                  <p className="text-xs text-gray-400">{new Date(p.createdAt).toLocaleDateString()}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                  p.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
